import React from 'react';
import { Rocket, Calendar, Sparkles, Lightbulb, CheckCircle, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const roadmapBuckets = [
  {
    title: 'Now Shipping',
    icon: <CheckCircle className="h-5 w-5 text-emerald-500" />,
    accent: 'from-emerald-500/10 to-emerald-100',
    items: [
      'Interactive story soundscapes with spatial audio.',
      'Help Center relaunch featuring live concierge cards.',
      'Offline-ready desktop canvas from MyStoryMe_PWA_Desktop.html.',
    ],
  },
  {
    title: 'Up Next',
    icon: <Rocket className="h-5 w-5 text-purple-500" />,
    accent: 'from-purple-500/10 to-purple-100',
    items: [
      'Shared journals so parents & kids annotate stories together.',
      'Instant classroom onboarding with roster imports.',
      'Adaptive reading pace suggestions powered by engagement data.',
    ],
  },
  {
    title: 'Exploring Later',
    icon: <Lightbulb className="h-5 w-5 text-amber-500" />,
    accent: 'from-amber-500/10 to-amber-100',
    items: [
      'Wearable bedtime remote to trigger audio chapters.',
      'Story translation packs curated by our global community.',
      'Holographic library preview for AR headsets.',
    ],
  },
];

const milestones = [
  {
    date: 'Q4 2025',
    title: 'Live Collaboration',
    detail: 'Multiple caretakers can edit prompts simultaneously with change tracking.',
  },
  {
    date: 'Q1 2026',
    title: 'Creator Marketplace',
    detail: 'Invite illustrators and educators to sell verified story bundles.',
  },
  {
    date: 'Q2 2026',
    title: 'Immersive Rooms',
    detail: 'Turn stories into ambient projections for bedtime or classroom circles.',
  },
];

const Roadmap = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50 flex flex-col">
      <Header />
      <main className="flex-1">
        <section className="container mx-auto px-4 py-16 lg:py-20">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-purple-700 font-semibold shadow">
              <Calendar className="h-4 w-4" />
              Product Roadmap
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-6 mb-4">
              The future of magical, parent-owned storytelling
            </h1>
            <p className="text-lg text-gray-600">
              Everything we ship keeps the cinematic feel of our desktop PWA concept—gradient halos, floating cards, and
              zero-compromise privacy.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {roadmapBuckets.map((bucket) => (
              <div
                key={bucket.title}
                className={`rounded-3xl border border-purple-100 bg-gradient-to-br ${bucket.accent} p-6 shadow-xl`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-2xl bg-white text-purple-600 flex items-center justify-center">
                    {bucket.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">{bucket.title}</h3>
                </div>
                <ul className="space-y-3 text-gray-700">
                  {bucket.items.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 text-purple-500">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="rounded-3xl border border-purple-100 bg-white p-8 shadow-2xl mb-16">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Milestone timeline</h2>
                <p className="text-gray-600">
                  Dates are estimates and may shift as we polish the experience. Expect the same obsessively designed UI as
                  the desktop PWA blueprint.
                </p>
              </div>
              <a
                href="/feedback"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold shadow-lg"
              >
                Suggest a feature
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {milestones.map((ms) => (
                <div key={ms.title} className="rounded-2xl border border-purple-50 bg-purple-50/60 p-5">
                  <p className="text-sm uppercase text-purple-500 font-semibold">{ms.date}</p>
                  <h3 className="text-xl font-bold text-gray-900 mt-2">{ms.title}</h3>
                  <p className="text-gray-600 mt-2">{ms.detail}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white rounded-3xl p-8 shadow-2xl">
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 px-4 py-1 rounded-full bg-white/10 text-sm uppercase tracking-wide mb-4">
                  <Sparkles className="h-4 w-4" />
                  Community-driven
                </div>
                <h3 className="text-3xl font-bold mb-4">
                  Vote on what we build next via the Feedback hub.
                </h3>
                <p className="text-white/80">
                  Share your dream features, beta-test new flows, and help us keep MyStoryMe magical on every device.
                </p>
              </div>
              <div className="flex flex-col gap-4 w-full md:w-auto">
                <a
                  href="/feedback"
                  className="px-6 py-3 rounded-xl bg-white text-purple-800 font-semibold shadow-lg text-center"
                >
                  Open Feedback
                </a>
                <a
                  href="/help"
                  className="px-6 py-3 rounded-xl border border-white/40 text-white font-semibold text-center"
                >
                  Visit Help Center
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Roadmap;


